import { useCallback, useEffect, useMemo, useState } from "react";
import { ListChecks, RefreshCw, Save } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { STAGE_REQUIRED_FIELDS_CONFIG_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

/**
 * Which lead fields must be filled before a lead can move into a stage.
 *
 * A grid: one row per field, one column per stage, tick the stages that need
 * it. A rep moving a lead into a ticked stage is asked for the field on the
 * Update Status form itself, and the move is refused until it is filled.
 *
 * Nothing is ticked by default — every rule here is one an admin chose, so a
 * tenant sees no new blocking until someone sets it.
 *
 * Applies to stage moves a person makes (Update Status, and the stage on the
 * add / edit dialog). The AI caller, bulk upload, onboarding and the lead
 * webhooks are not stopped, since there is nobody there to ask.
 */
export default function StageRequiredFieldsConfig() {
    const { fetch, update } = useApiMethods();

    const [fields, setFields]           = useState([]);
    const [stages, setStages]           = useState([]);
    const [partnerStages, setPartner]   = useState([]);
    const [rules, setRules]             = useState({});   // key -> Set(stage)
    const [showPartner, setShowPartner] = useState(false);
    const [canEdit, setCanEdit]         = useState(false);
    const [loading, setLoading]         = useState(false);
    const [saving, setSaving]           = useState(false);
    const [dirty, setDirty]             = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        fetch(STAGE_REQUIRED_FIELDS_CONFIG_URL)
            .then(res => {
                if (res?.status === "success") {
                    setFields(res.fields || []);
                    setStages(res.stages || []);
                    setPartner(res.partner_stages || []);
                    setCanEdit(!!res.can_edit);
                    const map = {};
                    (res.fields || []).forEach(f => { map[f.key] = new Set(f.stages || []); });
                    setRules(map);
                    // Open the partner columns when a rule already uses one, so
                    // a saved rule is never hidden from the admin editing it.
                    setShowPartner((res.fields || []).some(f =>
                        (f.stages || []).some(s => (res.partner_stages || []).includes(s))));
                    setDirty(false);
                } else {
                    ToastMsg("error", res?.message || "Failed to load mandatory fields");
                }
            })
            .catch(() => ToastMsg("error", "Failed to load mandatory fields"))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const columns = useMemo(
        () => (showPartner ? [...stages, ...partnerStages] : stages),
        [showPartner, stages, partnerStages]);

    const has = (key, stage) => !!rules[key]?.has(stage);

    const setCell = (key, stage, on) => {
        if (!canEdit) return;
        setRules(r => {
            const next = new Set(r[key] || []);
            on ? next.add(stage) : next.delete(stage);
            return { ...r, [key]: next };
        });
        setDirty(true);
    };

    // Ticks or clears every visible column for one field.
    const setRow = (key, on) => {
        if (!canEdit) return;
        setRules(r => {
            const next = new Set(r[key] || []);
            columns.forEach(s => (on ? next.add(s) : next.delete(s)));
            return { ...r, [key]: next };
        });
        setDirty(true);
    };

    // "From this stage onward" — the usual shape of a rule (state is needed
    // once a lead is Qualified and stays needed after), set in one click
    // instead of eight.
    const setFrom = (key, stage) => {
        if (!canEdit) return;
        const list = stages.includes(stage) ? stages : partnerStages;
        const from = list.indexOf(stage);
        setRules(r => {
            const next = new Set(r[key] || []);
            list.forEach((s, i) => (i >= from ? next.add(s) : next.delete(s)));
            return { ...r, [key]: next };
        });
        setDirty(true);
    };

    const save = () => {
        setSaving(true);
        const payload = {};
        fields.forEach(f => { payload[f.key] = Array.from(rules[f.key] || []); });
        update(STAGE_REQUIRED_FIELDS_CONFIG_URL, { fields: payload })
            .then(res => {
                if (res?.status === "success") {
                    ToastMsg("success", "Mandatory fields saved");
                    setDirty(false);
                } else ToastMsg("error", res?.message || "Could not save");
            })
            .catch(() => ToastMsg("error", "Could not save"))
            .finally(() => setSaving(false));
    };

    const ruleCount = fields.filter(f => (rules[f.key]?.size || 0) > 0).length;

    return (
        <ContainerCard>
            <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-2">
                        <ListChecks className="h-5 w-5 text-indigo-500" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                Mandatory Fields
                            </h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Lead details that must be filled before a lead moves into a stage.
                                {" "}{ruleCount} of {fields.length} field{fields.length === 1 ? "" : "s"} required somewhere.
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={load} title="Reload"
                            className="h-8 w-8 rounded-md border border-gray-200 dark:border-gray-600 text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center justify-center">
                            <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
                        </button>
                        {canEdit && (
                            <button onClick={save} disabled={!dirty || saving}
                                className="h-8 px-3 rounded-md text-xs font-medium bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 text-white flex items-center gap-1.5">
                                <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
                            </button>
                        )}
                    </div>
                </div>

                {!canEdit && (
                    <p className="mb-4 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                        Only a super admin can change these. You are seeing the current settings.
                    </p>
                )}

                <div className="mb-3 flex items-center justify-between gap-3 flex-wrap">
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 max-w-2xl">
                        Tick the stages that need each field. When a rep moves a lead into a ticked
                        stage, the Update Status form asks for the missing field and will not save
                        the move until it is filled. Double-click a box to tick that stage and every
                        stage after it. AI calls, bulk upload, onboarding and lead webhooks are not
                        blocked.
                    </p>
                    {partnerStages.length > 0 && (
                        <label className="flex items-center gap-1.5 text-[11px] text-gray-600 dark:text-gray-300 cursor-pointer">
                            <input type="checkbox" checked={showPartner}
                                onChange={e => setShowPartner(e.target.checked)} />
                            Show channel partner stages
                        </label>
                    )}
                </div>

                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead>
                            <tr className="bg-gray-50 dark:bg-gray-700/40">
                                <th className="sticky left-0 z-10 bg-gray-50 dark:bg-gray-800 text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300 min-w-[150px]">
                                    Field
                                </th>
                                {columns.map(s => (
                                    <th key={s}
                                        className={cn(
                                            "px-1.5 py-2 font-medium text-gray-600 dark:text-gray-300 text-center align-bottom",
                                            partnerStages.includes(s) && "bg-violet-50/60 dark:bg-violet-900/20"
                                        )}>
                                        <span className="block w-16 mx-auto leading-tight text-[10px]">{s}</span>
                                    </th>
                                ))}
                                <th className="px-2 py-2 text-[10px] font-medium text-gray-500 text-center">All</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                            {fields.map(f => {
                                const allOn = columns.length > 0 && columns.every(s => has(f.key, s));
                                const count = rules[f.key]?.size || 0;
                                return (
                                    <tr key={f.key} className="hover:bg-gray-50/60 dark:hover:bg-gray-700/20">
                                        <td className="sticky left-0 z-10 bg-white dark:bg-gray-800 px-3 py-2">
                                            <span className={cn(
                                                "font-medium",
                                                count ? "text-gray-800 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"
                                            )}>
                                                {f.label}
                                            </span>
                                            {count > 0 && (
                                                <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                                                    {count} stage{count === 1 ? "" : "s"}
                                                </span>
                                            )}
                                        </td>
                                        {columns.map(s => (
                                            <td key={s}
                                                className={cn("text-center px-1.5 py-2",
                                                    partnerStages.includes(s) && "bg-violet-50/40 dark:bg-violet-900/10")}>
                                                <input type="checkbox"
                                                    aria-label={`${f.label} required for ${s}`}
                                                    checked={has(f.key, s)}
                                                    disabled={!canEdit}
                                                    onChange={e => setCell(f.key, s, e.target.checked)}
                                                    onDoubleClick={() => setFrom(f.key, s)}
                                                    className="h-3.5 w-3.5 accent-indigo-600 cursor-pointer disabled:cursor-default" />
                                            </td>
                                        ))}
                                        <td className="text-center px-2 py-2">
                                            {canEdit && (
                                                <button onClick={() => setRow(f.key, !allOn)}
                                                    className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline whitespace-nowrap">
                                                    {allOn ? "Clear" : "All"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {!fields.length && !loading && (
                                <tr><td colSpan={columns.length + 2}
                                    className="px-3 py-6 text-center text-gray-400">No fields</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </ContainerCard>
    );
}
